import React, { useState, useMemo, useLayoutEffect, useRef } from "react";
import {
  CountryCode,
  isSupportedCountry,
  getCountryCallingCode,
  parsePhoneNumberFromString
} from "libphonenumber-js";
import cn from "clsx";
import { InputCustomChangeEventHandler } from "../../types";
import {
  get,
  hasAndIsNotEmpty,
  isEmpty,
  calcTotalValidProperties
} from "../../utils/helpers";
import { ISO2_TO_ISO3, getIso3, ISO3_TO_ISO2 } from "../../utils/countries";
import { loadCountries } from "../../utils/api/countries";
import useT from "../../hooks/useT";
import useAsyncRetryMax from "../../hooks/useAsyncRetryMax";
import InputSelect from "../InputSelect";
import InputSelectCustom, {
  ReactSelectOptionObject
} from "../InputSelectCustom";
import InputText from "../InputText";
import FieldLabel from "../Field/Label";
import "./InputPhone.css";
//import debug from "../../utils/debug";

export interface InputPhoneProps {
  /** The ID of the input. */
  id: string;
  /** The name of the input. */
  name: string;
  /** The input's value. */
  value: string;
  /** Labels to display for each child input. */
  labels?: {
    dialCode?: string;
    phoneNumber?: string;
  };
  /** Extralabels to display for each child input. */
  extraLabels?: string | string[];
  /** Extra class names to assign to the input. */
  className?: string;
  /**
   * A specific country to use to determine the dial code (in
   * ISO 3166-1 Alpha 2 format).
   */
  country?: string | null;
  /**
   * The language (will default to the i18next language value if not set).
   */
  lang?: string;
  /** Boolean to enable the clear functionality. */
  clearable?: boolean;
  /** Boolean to enable the search functionality. */
  searchable?: boolean;
  /** Boolean to mark if inputs are interactive. */
  disabled?: boolean;
  /** Event handler for when a child input is focused. */
  onFocus?: React.FocusEventHandler;
  /** Event handler for when a child input is blurred. */
  onBlur?: React.FocusEventHandler;
  /**
   * Custom event handler to mark that the value was changed.
   *
   * This will combine the dial code and the number (if necessary)
   * to produce the final phone value.
   */
  onChange?: InputCustomChangeEventHandler;
  /**
   * Event handler for when a child input is cleared. Currently only
   * the phone number field is supported.
   */
  onClear?: React.MouseEventHandler;
  onCustomLable?: (text: string) => void;
  onClaerCustomLabel?: () => void;
}

export interface InputPhoneParts {
  phoneNumber: string | null | undefined;
  phone: string | null | undefined;
  country: string | null | undefined;
}
export type InputPhoneFields = "phone" | "phoneNumber" | "country";

const COUNTRY_DIAL_CODE_OPTIONS: ReactSelectOptionObject[] = [
  {
    label: "",
    value: null
  }
];
Object.keys(ISO2_TO_ISO3).forEach(iso2 => {
  if (isSupportedCountry(iso2)) {
    const dialCode = getCountryCallingCode(iso2 as CountryCode);
    COUNTRY_DIAL_CODE_OPTIONS.push({
      label: `+${dialCode}`,
      value: iso2
    });
  }
});

/**
 * Check if the phone number has a dial code already.
 */
export const phoneNumberHasDialCode = (input: string): boolean =>
  !!input &&
  input.length > 1 &&
  (input[0] === "+" || input.slice(0, 2) === "00");

/**
 * Parse a phone number into its component parts.
 */
export const parsePhoneParts = (
  phone: string | null | undefined,
  country: string | null | undefined
): InputPhoneParts => {
  if (phone) {
    const phoneNumber = parsePhoneNumberFromString(
      phone,
      !phoneNumberHasDialCode(phone)
        ? country
          ? (country as CountryCode)
          : undefined
        : undefined
    );

    if (phoneNumber) {
      return {
        phoneNumber: String(phoneNumber.number),
        phone: phoneNumber.formatNational(),
        country: phoneNumber.country || country
      };
    }
  }

  return {
    phoneNumber: phone,
    phone,
    country
  };
};

export default function InputPhone({
  id,
  name,
  value,
  labels = {},
  className,
  country = null,
  lang,
  clearable = true,
  searchable = false, //by default - other masions will not have the option to search on list of country code, unless they demand this option
  disabled,
  onFocus,
  onBlur,
  onChange,
  onClear,
  extraLabels,
  onClaerCustomLabel,
  onCustomLable,
  ...inputProps
}: InputPhoneProps) {
  // Shared/internal context/state
  const { t, i18n } = useT("countries");
  const [focused, setFocused] = useState({
    country: false,
    phone: false
  });
  if (country && country.length === 3) {
    country = ISO3_TO_ISO2[country];
  }
  const ref = useRef({
    external: parsePhoneParts(value, country),
    triggerChange: () => {}
  });
  const [current, setCurrent] = useState<InputPhoneParts>(ref.current.external);
  const [userTouchedCountry, setUserTouchedCountry] = useState(false);
  const hasValue = !!value && !!value.trim();
  const hasCurrentPhone = !!current.phone && !!current.phone.trim();
  const lng = lang || i18n.language;

  // Trigger the phone number to potentially change
  ref.current.triggerChange = () => {
    // The external number is what we had in the last change, which is changed on
    // the form and then fed back down to this component.
    const { external } = ref.current;

    // Use the current (local state) version of the phone components.
    // If they are not set (i.e. empty) then use the external phone
    // components.
    const check = parsePhoneParts(
      // "" is falsey, however we want to use it to clear the value if it is set within
      // the component.
      current.phone === "" ? "" : current.phone || external.phone,
      // Only use the current country value if it has changed from the prop given
      userTouchedCountry ? current.country : external.country
    );

    // Update the internal state
    if (
      current.phone !== check.phone ||
      current.country !== check.country ||
      current.phoneNumber !== check.phoneNumber
    ) {
      setCurrent(check);
    }

    // Publish the change
    if (onChange) {
      if (onClaerCustomLabel) {
        onClaerCustomLabel();
      }
      onChange(check.phoneNumber || null, name);
    }
  };

  const getCountryLabel = (iso2: string) => {
    if (!iso2) {
      return "";
    }
    let label = t(iso2, { lng });
    if (label === iso2) {
      const iso3 = getIso3(iso2);
      if (iso3) {
        label = t(iso3, { lng });
        if (label === iso3) {
          label = "";
        }
      }
    }
    return label;
  };

  const countries = useAsyncRetryMax(() => loadCountries(), 3, []);
  const dialCodeOptions = useMemo<ReactSelectOptionObject[]>(() => {
    const _dialCodeOptions: ReactSelectOptionObject[] = [
      // Null/empty/unset option
      COUNTRY_DIAL_CODE_OPTIONS[0]
    ];

    COUNTRY_DIAL_CODE_OPTIONS.forEach(option => {
      // Only display options which have a country label
      const translationId = String(option.value);
      const countryLabel = getCountryLabel(translationId);
      if (countryLabel && countryLabel !== translationId) {
        // Add the country name of the label if it's not the current value
        if (current.country !== option.value) {
          _dialCodeOptions.push({
            label: `${countryLabel} ${option.label}`,
            value: option.value
          });
        } else {
          _dialCodeOptions.push(option);
        }
      }
    });

    return _dialCodeOptions;
  }, [countries.value, current.country]);

  // Event handlers
  // -- Handle changing the country dial code to then update the value
  const handleChangeDialCode = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    // Mark that the user specifically changed the country code,
    // so that any external value doesn't override it

    setUserTouchedCountry(true);

    setCurrent({
      ...current,
      country: !isEmpty(event.target.value) ? String(event.target.value) : ""
    });
  };
  const handleSearchChangeDialCode = (
    event: React.ChangeEvent<HTMLSelectElement>
  ) => {
    // Mark that the user specifically changed the country code,
    // so that any external value doesn't override it
    setUserTouchedCountry(true);

    setCurrent({
      ...current,
      country: !isEmpty(event) ? String(event) : ""
    });
  };
  // -- Change the value if the input is changed
  const handleChangePhoneNumber = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setCurrent({
      ...current,
      phone: event.target.value || ""
    });
  };

  // -- If any input is focused, manage focus state
  const handleFocus = (fieldKey: InputPhoneFields): React.FocusEventHandler => (
    event: React.FocusEvent
  ) => {
    const updateFocused = { country: false, phone: false };
    setFocused({ ...updateFocused, [fieldKey]: true });
    if (onFocus) {
      onFocus(event);
    }
  };

  // -- If any input is blurred, reset focus state
  const handleBlur = (event: React.FocusEvent) => {
    setFocused({ country: false, phone: false });
    if (onBlur) {
      onBlur(event);
    }

    // Publish the change when a field is blurred.
    ref.current.triggerChange();

    // This setTimeout trick ensures it's done at a specific point in the
    // JS event loop. Without this, it seems to have some weird bugs.
    // Shai Fisher 10/08/2022: timeout cancelled, call immediately to prevent submit without phone:
    //   When user enters phone in mobile device and immidiately press the submit
    //   then he is redirected to next screen without phone
    //setTimeout(() => ref.current.triggerChange(), 0);
  };

  // -- Handle the clear event
  const handleClear = (fieldKey: InputPhoneFields): React.MouseEventHandler => (
    event: React.MouseEvent
  ) => {
    const updateCurrent = {
      ...current
    };

    switch (fieldKey) {
      case "phone":
      case "phoneNumber":
        ref.current.external.phone = "";
        ref.current.external.phoneNumber = "";
        updateCurrent.phone = "";
        updateCurrent.phoneNumber = "";
        // Clearing the phone number will reset the dial code/country as well
        ref.current.external.country = country;
        setUserTouchedCountry(false);
        break;

      case "country":
        updateCurrent.country = "";
        // Revert external dial code/country back to the prop dial code/country
        ref.current.external.country = country;
        setUserTouchedCountry(false);
        break;
    }

    setCurrent(updateCurrent);

    if (onClear) {
      onClear(event);
    }

    // Publish the change when a field is cleared.
    // This setTimeout trick ensures it's done at a specific point in the
    // JS event loop. Without this, it seems to have some weird bugs.
    setTimeout(() => ref.current.triggerChange(), 0);
  };

  // Any external changes via props can potentially trigger a new change
  useLayoutEffect(() => {
    ref.current.external = parsePhoneParts(value, country);
    // This setTimeout trick ensures it's done at a specific point in the
    // JS event loop. Without this, it seems to have some weird bugs.
    setTimeout(() => ref.current.triggerChange(), 0);
  }, [value, country]);

  return (
    <div
      className={cn(
        "input-phone",
        calcTotalValidProperties(labels) > 0 && "has-labels",
        calcTotalValidProperties(focused) > 0 && "is-focused",
        hasValue && "has-value",
        !!disabled && "is-disabled",
        className
      )}
    >
      <div
        className={cn(
          "subfield input-phonenumber",
          hasCurrentPhone && "has-value",
          focused.phone && "is-focused",
          labels.phoneNumber && "has-label"
        )}
      >
        {hasAndIsNotEmpty(labels, "phoneNumber") && (
          <FieldLabel
            id={`${id}-phoneNumber`}
            label={labels.phoneNumber}
            className="subfield-label"
            extraLabels={extraLabels}
          />
        )}
        <InputText
          {...inputProps}
          className="input-control"
          type="tel"
          id={`${id}-phoneNumber`}
          name="phoneNumber"
          value={current.phone || ""}
          disabled={disabled}
          clearable={clearable}
          onFocus={handleFocus("phone")}
          onBlur={handleBlur}
          onChange={handleChangePhoneNumber}
          onClear={handleClear("phone")}
          aria-owns={`${id}-dialCode`}
          aria-required={!!get(extraLabels, "required")}
          aria-label={labels.phoneNumber}
        />
      </div>
      <div className="subfield separator" />
      <div
        className={cn(
          "subfield input-dialcode",
          current.country && "has-value",
          focused.country && "is-focused",
          labels.dialCode && "has-label"
        )}
      >
        {hasAndIsNotEmpty(labels, "dialCode") && (
          <FieldLabel
            id={`${id}-dialCode`}
            label={labels.dialCode}
            className="subfield-label"
            extraLabels={extraLabels}
          />
        )}
        {searchable && (
          <InputSelectCustom
            id={`${id}-dialCode`}
            name="dialCode"
            value={current.country}
            options={dialCodeOptions}
            className="input-control"
            disabled={disabled}
            clearable={userTouchedCountry && clearable}
            searchable={searchable}
            lang={lng}
            onFocus={handleFocus("country")}
            onBlur={handleBlur}
            onChange={handleSearchChangeDialCode}
            onClear={handleClear("phone")}
            aria-required={!!get(extraLabels, "required")}
            aria-label={labels.dialCode}
          />
        )}
        {!searchable && (
          <InputSelect
            id={`${id}-dialCode`}
            name="dialCode"
            className="input-control"
            value={current.country}
            options={dialCodeOptions}
            disabled={disabled}
            clearable={userTouchedCountry && clearable}
            lang={lng}
            onFocus={handleFocus("country")}
            onBlur={handleBlur}
            onChange={handleChangeDialCode}
            onClear={handleClear("country")}
            aria-required={!!get(extraLabels, "required")}
            aria-label={labels.dialCode}
          />
        )}
      </div>
    </div>
  );
}
